<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'CreateMeterReadingsParameters',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    labelAlign: {
      type: String,
      required: false,
      default: 'right',
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const params = ref({
    notes: null,
    date_override: null,
  });
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'md',
  }));

  const $data = computed(() => ({
    params: params.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      {
        name: 'params',
        cache: { type: 'local', key: 'CreateMeterReadingsParameters' },
      },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: {},
    ux: {},
    dataRefs: {
      params,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext('CreateMeterReadings', 'Create Meter Readings'),
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.labelAlign,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('labelAlign');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_famhsz8ku_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeComponent_wn9d0ed4e_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        close: true,
        ok: true,
        value: params.value,
      },
    };
  };

  const act_7315092382398562_closeModal_famhsz8ku =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_famhsz8ku_getActionArgs,
    });
  const inst_evh_7315092382398562_close_p57s22ire =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_famhsz8ku,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_p57s22ire],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_9oh6fp0ai =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_yzn23twcm =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_9oh6fp0ai,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_yzn23twcm],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_bqv3d0vpe =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_5q1y409b2 =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_bqv3d0vpe,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_5q1y409b2],
    event: 'close',
  });
  const act_1040167445267876_closeComponent_wn9d0ed4e =
    new actionTypes.CloseComponentAction({
      actionArgs: act_1040167445267876_closeComponent_wn9d0ed4e_getActionArgs,
      displayName: 'submit',
    });
  const inst_evh_1040167445267876_submit_8dc0io4hq =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeComponent_wn9d0ed4e,
      event: 'submit',
      displayName: 'submit',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_8dc0io4hq],
    event: 'submit',
  });
  const act_2434514588044777_closeComponent_7ul9xa55m =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2434514588044777_clickPreventStop_lxbf82afb =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_7ul9xa55m,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_lxbf82afb],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_p57s22ire,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_yzn23twcm,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_5q1y409b2,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_submit_8dc0io4hq,
    submit: inst_evh_1040167445267876_submit_8dc0io4hq,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_lxbf82afb,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_p57s22ire = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_p57s22ire.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_yzn23twcm = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_yzn23twcm.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_5q1y409b2 = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_5q1y409b2.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_8dc0io4hq = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_8dc0io4hq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_lxbf82afb = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_lxbf82afb.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="CreateMeterReadingsParameters"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        name="createOptions"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpFormGroup
          :label-cols-md="4"
          :content-cols-md="8"
          name="notes"
          :label="pgettext('CreateMeterReadings', 'Notes')"
          :labelAlign="labelAlign"
        >
          <ItpFormTextarea name="notes" v-model="params.notes">
          </ItpFormTextarea>
        </ItpFormGroup>
        <ItpFormGroup
          :label-cols-md="4"
          :content-cols-md="8"
          name="dateOverride"
          :label="pgettext('CreateMeterReadings', 'Reading date')"
          :description="
            pgettext(
              'CreateMeterReadings',
              'Use this date as reading date instead.'
            )
          "
          :labelAlign="labelAlign"
        >
          <ItpFormDatetime
            name="dateOverride"
            clearable
            v-model="params.date_override"
          >
          </ItpFormDatetime>
        </ItpFormGroup>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :form="forms.createOptions && forms.createOptions.id"
          :text="pgettext('MeterMeasurementGenerateButton', 'Create Readings')"
          type="submit"
          variant="primary"
          :spinning="actions.submit.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="pgettext('CreateMeterReadings', 'Cancel')"
          variant="default"
          :disabled="actions.submit.isRunning"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
